// frontend/dispatcher-client/src/components/maps/LiveTransportsMap.js

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import maplibregl from "maplibre-gl";

import {
  ensureRoadReferenceOverlay,
} from "./roadReferenceOverlay";

import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Container,
  MapPin,
  RefreshCw,
  Route,
  Search,
  Truck,
  User,
} from "lucide-react";

import { useTranslation } from "react-i18next";

import api from "../../axiosConfig";

import {
  getSocket,
} from "../../lib/socket";

import {
  getStoredToken,
  getStoredUser,
} from "../../lib/authStorage";

import DriverRoutePlannerMap from "./DriverRoutePlannerMap";

import "maplibre-gl/dist/maplibre-gl.css";
import "../../styles/MapBase.css";
import "../../styles/LiveTransportsMap.css";

const MAP_STYLE =
  "/maps/styles/loadevo-road.json?v=5";

const DEFAULT_CENTER = [
  -8.61099,
  41.14961,
];

const DEFAULT_ZOOM = 5;

const ROUTES_SOURCE_ID =
  "live-transports-routes";

const ROUTES_CASING_LAYER_ID =
  "live-transports-routes-casing";

const ROUTES_LINE_LAYER_ID =
  "live-transports-routes-line";

const POINTS_SOURCE_ID =
  "live-transports-points";

const POINTS_CLUSTER_LAYER_ID =
  "live-transports-points-cluster";

const POINTS_CLUSTER_COUNT_LAYER_ID =
  "live-transports-points-cluster-count";

const POINTS_HALO_LAYER_ID =
  "live-transports-points-halo";

const POINTS_CIRCLE_LAYER_ID =
  "live-transports-points-circle";

const POINTS_LABEL_LAYER_ID =
  "live-transports-points-label";

const FINAL_STATUSES = new Set([
  "COMPLETED",
  "CANCELLED",
]);

const LIVE_SESSIONS_CACHE_PREFIX =
  "loadevo.map.live-sessions.v4.";

const LIVE_SESSIONS_CACHE_SOFT_TTL_MS =
  10 * 60 * 1000;

const LIVE_SESSIONS_CACHE_HARD_TTL_MS =
  24 * 60 * 60 * 1000;

function decodeLiveMapJwt(
  token
) {
  try {
    const payloadPart =
      String(
        token || ""
      ).split(".")[1];

    if (!payloadPart) {
      return null;
    }

    const normalized =
      payloadPart
        .replace(
          /-/g,
          "+"
        )
        .replace(
          /_/g,
          "/"
        );

    const padded =
      normalized +
      "=".repeat(
        (
          4 -
          (
            normalized.length %
            4
          )
        ) %
          4
      );

    return JSON.parse(
      window.atob(
        padded
      )
    );
  } catch {
    return null;
  }
}

function resolveLiveMapCompanyId() {
  try {
    const token =
      getStoredToken() ||
      window.localStorage.getItem(
        "authToken"
      ) ||
      "";

    const payload =
      token
        ? decodeLiveMapJwt(
            token
          )
        : null;

    const fromJwt =
      payload?.company_id ??
      payload?.companyId ??
      null;

    if (
      Number.isFinite(
        Number(
          fromJwt
        )
      ) &&
      Number(
        fromJwt
      ) > 0
    ) {
      return Number(
        fromJwt
      );
    }

    const direct =
      window.sessionStorage.getItem(
        "companyId"
      ) ||
      window.sessionStorage.getItem(
        "company_id"
      ) ||
      window.localStorage.getItem(
        "companyId"
      ) ||
      window.localStorage.getItem(
        "company_id"
      ) ||
      null;

    if (
      Number.isFinite(
        Number(
          direct
        )
      ) &&
      Number(
        direct
      ) > 0
    ) {
      return Number(
        direct
      );
    }

    const storedUser =
      getStoredUser();

    const fromUser =
      storedUser?.company_id ??
      storedUser?.companyId ??
      null;

    if (
      Number.isFinite(
        Number(
          fromUser
        )
      ) &&
      Number(
        fromUser
      ) > 0
    ) {
      return Number(
        fromUser
      );
    }
  } catch {
    // Sem company context.
  }

  return null;
}

function getLiveSessionsCacheKey() {
  const companyId =
    resolveLiveMapCompanyId();

  if (!companyId) {
    return null;
  }

  return (
    LIVE_SESSIONS_CACHE_PREFIX +
    `company:${companyId}`
  );
}

function readLiveSessionsCache(
  cacheKey
) {
  if (!cacheKey) {
    return null;
  }

  try {
    const raw =
      window.localStorage.getItem(
        cacheKey
      );

    if (!raw) {
      return null;
    }

    const parsed =
      JSON.parse(raw);

    const savedAt =
      Number(
        parsed?.savedAt
      );

    if (
      !Number.isFinite(
        savedAt
      ) ||
      !Array.isArray(
        parsed?.sessions
      )
    ) {
      window.localStorage.removeItem(
        cacheKey
      );

      return null;
    }

    const age =
      Date.now() -
      savedAt;

    /*
     * SWR:
     * Até 10 min = fresh.
     * 10 min → 24 h = mostramos imediatamente,
     * mas validamos em background.
     * > 24 h = demasiado antigo para mostrar.
     */
    if (
      age >
      LIVE_SESSIONS_CACHE_HARD_TTL_MS
    ) {
      window.localStorage.removeItem(
        cacheKey
      );

      return null;
    }

    return {
      sessions:
        parsed.sessions,

      lastUpdated:
        parsed.lastUpdated ||
        new Date(
          savedAt
        ).toISOString(),

      loadsRev:
        Number.isFinite(
          Number(
            parsed.loadsRev
          )
        )
          ? Number(
              parsed.loadsRev
            )
          : null,

      stale:
        age >
        LIVE_SESSIONS_CACHE_SOFT_TTL_MS,
    };
  } catch (
    error
  ) {
    console.warn(
      "[LiveTransportsMap] Invalid cache:",
      error
    );

    return null;
  }
}

function writeLiveSessionsCache(
  cacheKey,
  {
    sessions,
    lastUpdated,
    loadsRev = null,
  }
) {
  if (
    !cacheKey ||
    !Array.isArray(
      sessions
    )
  ) {
    return;
  }

  try {
    window.localStorage.setItem(
      cacheKey,
      JSON.stringify({
        version:
          4,

        savedAt:
          Date.now(),

        lastUpdated:
          lastUpdated instanceof Date
            ? lastUpdated.toISOString()
            : lastUpdated ||
              new Date().toISOString(),

        loadsRev:
          Number.isFinite(
            Number(
              loadsRev
            )
          )
            ? Number(
                loadsRev
              )
            : null,

        sessions,
      })
    );
  } catch (
    error
  ) {
    console.warn(
      "[LiveTransportsMap] Cache write failed:",
      error?.message
    );
  }
}

function getArrayPayload(data) {
  if (Array.isArray(data)) {
    return data;
  }

  const candidates = [
    data?.items,
    data?.rows,
    data?.loads,
    data?.sessions,
    data?.pickupDeliveries,
    data?.data,
  ];

  const found =
    candidates.find(Array.isArray);

  if (found) {
    return found;
  }

  if (
    data &&
    typeof data === "object"
  ) {
    const nestedArrays =
      Object.values(data).filter(
        Array.isArray
      );

    if (nestedArrays.length) {
      return nestedArrays.flat();
    }
  }

  return [];
}

function parseMaybeJson(value) {
  if (
    value === null ||
    value === undefined
  ) {
    return null;
  }

  if (typeof value === "object") {
    return value;
  }

  if (typeof value !== "string") {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function toFiniteNumberOrNull(value) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return null;
  }

  const number = Number(
    String(value).replace(",", ".")
  );

  return Number.isFinite(number)
    ? number
    : null;
}

function getTransportSessionId(row) {
  const rawId =
    row?.transportsessionId ??
    row?.transportSessionId ??
    row?.transportsession_id ??
    row?.transport_session_id ??
    row?.transportSession?.id ??
    null;

  const id = Number(rawId);

  return Number.isInteger(id) &&
    id > 0
    ? id
    : null;
}

function normalizeLatLngPair(
  firstValue,
  secondValue
) {
  const first =
    toFiniteNumberOrNull(
      firstValue
    );

  const second =
    toFiniteNumberOrNull(
      secondValue
    );

  if (
    first === null ||
    second === null
  ) {
    return null;
  }

  /*
   * Formato habitual da aplicação:
   * latitude, longitude
   *
   * Exemplo:
   * 41.14961, -8.61099
   */
  if (
    Math.abs(first) <= 90 &&
    Math.abs(second) <= 180
  ) {
    return {
      lat: first,
      lng: second,
    };
  }

  /*
   * Fallback para GeoJSON:
   * longitude, latitude
   */
  if (
    Math.abs(first) <= 180 &&
    Math.abs(second) <= 90
  ) {
    return {
      lat: second,
      lng: first,
    };
  }

  return null;
}

function normalizeCoordinates(value) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return null;
  }

  /*
   * Quando existem campos com nomes
   * explícitos, são sempre prioritários.
   */
  if (
    value &&
    typeof value === "object" &&
    !Array.isArray(value)
  ) {
    const directLat =
      value.lat ??
      value.latitude ??
      value.placeLatitude ??
      value.latestStatusEvent
        ?.placeLatitude ??
      null;

    const directLng =
      value.lng ??
      value.lon ??
      value.longitude ??
      value.placeLongitude ??
      value.latestStatusEvent
        ?.placeLongitude ??
      null;

    const directCoordinates =
      normalizeLatLngPair(
        directLat,
        directLng
      );

    if (directCoordinates) {
      return directCoordinates;
    }
  }

  const parsed =
    parseMaybeJson(value);

  if (
    parsed &&
    parsed !== value
  ) {
    return normalizeCoordinates(
      parsed
    );
  }

  if (
    Array.isArray(value) &&
    value.length >= 2
  ) {
    return normalizeLatLngPair(
      value[0],
      value[1]
    );
  }

  if (
    value &&
    typeof value === "object"
  ) {
    return normalizeLatLngPair(
      value.lat ??
        value.latitude ??
        value[0],

      value.lng ??
        value.lon ??
        value.longitude ??
        value[1]
    );
  }

  const matches =
    String(value)
      .trim()
      .match(
        /-?\d+(?:\.\d+)?/g
      );

  if (
    !matches ||
    matches.length < 2
  ) {
    return null;
  }

  return normalizeLatLngPair(
    matches[0],
    matches[1]
  );
}

function normalizePoint(row, index) {
  const coordinates =
    normalizeCoordinates(
      row?.coordinates
    ) ||
    normalizeCoordinates(row);

  if (!coordinates) {
    return null;
  }

  const type = String(
    row?.type ||
    row?.pointType ||
    "STRATEGICPOINT"
  )
    .trim()
    .toUpperCase();

  return {
    id:
      row?.id ??
      `point-${index}`,

    transportsessionId:
      getTransportSessionId(row),

    sequence:
      Number(row?.sequence) ||
      index + 1,

    type,

    status:
      String(row?.status || "")
        .trim()
        .toUpperCase(),

    name:
      row?.location ||
      row?.name ||
      row?.city ||
      null,

    address:
      row?.address ||
      null,

    city:
      row?.city ||
      row?.placeCity ||
      null,

    country:
      row?.country ||
      row?.countryName ||
      row?.placeCountry ||
      null,

    countryCode:
      row?.countryCode ||
      row?.country_code ||
      row?.placeCountryCode ||
      null,

    postalCode:
      row?.postalCode ||
      row?.postcode ||
      row?.zipCode ||
      row?.zip_code ||
      row?.placePostalCode ||
      null,

    date:
      row?.date ||
      null,

    hour:
      row?.hour ||
      null,

    substatus:
      row?.latestStatusEvent
        ?.substatus ||
      row?.substatus ||
      null,

    truckPlate:
      row?.truckPlate ||
      row?.truck?.plate ||
      row?.truck?.truckPlate ||
      row?.truckArrivalPlate ||
      row?.truckDeparturePlate ||
      null,

    trailerPlate:
      row?.trailerPlate ||
      row?.trailer?.trailerPlate ||
      row?.trailer?.plate ||
      row?.trailerArrivalPlate ||
      row?.trailerDeparturePlate ||
      null,

    driverName:
      row?.driverName ||
      row?.driver?.name ||
      row?.driverNameArrival ||
      row?.driverNameDeparture ||
      null,

    lat:
      coordinates.lat,

    lng:
      coordinates.lng,
  };
}

function normalizeSnapshotPoint(
  point,
  index
) {
  const coordinates =
    normalizeCoordinates({
      lat:
        point?.lat ??
        point?.latitude,

      lng:
        point?.lng ??
        point?.lon ??
        point?.longitude,
    });

  if (!coordinates) {
    return null;
  }

  return {
    id:
      point?.templatePointId ??
      point?.id ??
      `snapshot-${index}`,

    sequence:
      Number(point?.sequence) ||
      index + 1,

    type:
      String(
        point?.type || "VIA"
      ).toUpperCase(),

    status: "",

    name:
      point?.name ||
      point?.city ||
      null,

    address:
      point?.address ||
      null,

    city:
      point?.city ||
      null,

    country:
      point?.country ||
      point?.countryName ||
      null,

    countryCode:
      point?.countryCode ||
      point?.country_code ||
      null,

    postalCode:
      point?.postalCode ||
      point?.postcode ||
      point?.zipCode ||
      point?.zip_code ||
      null,

    date: null,
    hour: null,
    substatus: null,
    truckPlate: null,
    trailerPlate: null,
    driverName: null,

    lat:
      coordinates.lat,

    lng:
      coordinates.lng,
  };
}

function normalizeGeometry(value) {
  const parsed =
    parseMaybeJson(value) ||
    value;

  if (
    parsed?.type === "Feature" &&
    parsed.geometry?.type ===
      "LineString" &&
    Array.isArray(
      parsed.geometry.coordinates
    )
  ) {
    return parsed;
  }

  if (
    parsed?.type === "LineString" &&
    Array.isArray(
      parsed.coordinates
    )
  ) {
    return {
      type: "Feature",

      geometry: parsed,

      properties: {},
    };
  }

  return null;
}

function normalizeRouteResponse(data) {
  const routes =
    Array.isArray(data?.routes)
      ? data.routes
      : [];

  const route =
    routes[0] ||
    data?.route ||
    null;

  return normalizeGeometry(
    route?.geometry
  );
}

function getPointLabel(point) {
  if (point.type === "PICKUP") {
    return "C";
  }

  if (point.type === "DELIVERY") {
    return "D";
  }

  if (point.type === "ORIGIN") {
    return "A";
  }

  if (point.type === "DESTINATION") {
    return "B";
  }

  if (point.type === "FUEL") {
    return "F";
  }

  return String(
    point.sequence || ""
  );
}

function getSessionDisplayNumber(
  session
) {
  const number =
    session?.transportSessionNumber ??
    session?.transportsessionNumber ??
    session?.sessionNumber ??
    session?.id;

  return `TS-${String(
    number || ""
  ).padStart(4, "0")}`;
}

function getSessionStatus(session) {
  return String(
    session?.status || "ACTIVE"
  )
    .trim()
    .toUpperCase();
}

function calculateProgress(points) {
  if (!points.length) {
    return 0;
  }

  const finished =
    points.filter((point) =>
      FINAL_STATUSES.has(
        String(
          point.status || ""
        ).toUpperCase()
      )
    ).length;

  return Math.round(
    (finished / points.length) * 100
  );
}

function calculateCompletedPointCount(
  points
) {
  return points.filter((point) =>
    FINAL_STATUSES.has(
      String(
        point?.status || ""
      ).toUpperCase()
    )
  ).length;
}

function getNextOperationalPoint(
  points
) {
  if (!points.length) {
    return null;
  }

  return (
    points.find(
      (point) =>
        !FINAL_STATUSES.has(
          String(
            point?.status || ""
          ).toUpperCase()
        )
    ) ||
    points[
      points.length - 1
    ] ||
    null
  );
}

function formatPointDateTime(
  point,
  locale
) {
  if (!point) {
    return "";
  }

  let dateLabel =
    point.date || "";

  const isoDateMatch =
    String(dateLabel).match(
      /^(\d{4})-(\d{2})-(\d{2})/
    );

  if (isoDateMatch) {
    const [, year, month, day] =
      isoDateMatch;

    try {
      dateLabel =
        new Date(
          Number(year),
          Number(month) - 1,
          Number(day)
        ).toLocaleDateString(
          locale
        );
    } catch {
      dateLabel =
        point.date || "";
    }
  }

  const hourLabel =
    point.hour
      ? String(point.hour).slice(
          0,
          5
        )
      : "";

  return [
    dateLabel,
    hourLabel,
  ]
    .filter(Boolean)
    .join(" · ");
}

function getPointLocationMeta(
  point
) {
  if (!point) {
    return "";
  }

  const city =
    String(
      point.city || ""
    ).trim();

  const country =
    String(
      point.country || ""
    ).trim();

  return (
    [city, country]
      .filter(Boolean)
      .join(", ") ||
    point.address ||
    ""
  );
}

function getPointCountryCode(
  point
) {
  const directCode =
    String(
      point?.countryCode || ""
    )
      .trim()
      .toUpperCase();

  if (
    /^[A-Z]{2}$/.test(
      directCode
    )
  ) {
    return directCode;
  }

  const searchableText =
    [
      point?.country,
      point?.address,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

  const countries = [
    ["portugal", "PT"],
    ["espanha", "ES"],
    ["spain", "ES"],
    ["frança", "FR"],
    ["france", "FR"],
    ["bélgica", "BE"],
    ["belgium", "BE"],
    ["países baixos", "NL"],
    ["netherlands", "NL"],
    ["holanda", "NL"],
    ["alemanha", "DE"],
    ["germany", "DE"],
    ["luxemburgo", "LU"],
    ["luxembourg", "LU"],
    ["suíça", "CH"],
    ["switzerland", "CH"],
    ["itália", "IT"],
    ["italy", "IT"],
  ];

  const match =
    countries.find(
      ([countryName]) =>
        searchableText.includes(
          countryName
        )
    );

  return match?.[1] || "";
}

function getCountryFlag(
  point
) {
  const countryCode =
    getPointCountryCode(
      point
    );

  if (!countryCode) {
    return "";
  }

  return countryCode.replace(
    /[A-Z]/g,
    (character) =>
      String.fromCodePoint(
        127397 +
          character.charCodeAt(
            0
          )
      )
  );
}

function getOperationalStatusText(
  point,
  t
) {
  if (!point) {
    return "";
  }

  const location =
    point.city ||
    point.name ||
    point.address ||
    t(
      "map.live.nextLocation",
      {
        defaultValue:
          "o próximo ponto",
      }
    );

  const substatus =
    String(
      point.substatus || ""
    )
      .trim()
      .toUpperCase();

  if (
    substatus ===
      "IN_TRANSIT_MOVING_TO_PICKUP_LOCATION" ||
    substatus ===
      "IN_TRANSIT_MOVING_TO_DELIVERY_LOCATION"
  ) {
    return t(
      "map.live.operationalStatus.movingTo",
      {
        location,

        defaultValue:
          "Em trânsito para {{location}}",
      }
    );
  }

  if (
    substatus ===
      "AT_PICKUP_LOCATION" ||
    substatus ===
      "AT_DELIVERY_LOCATION"
  ) {
    return t(
      "map.live.operationalStatus.atLocation",
      {
        location,

        defaultValue:
          "No local: {{location}}",
      }
    );
  }

  if (
    substatus ===
    "WAITING_TO_LOAD"
  ) {
    return t(
      "map.live.operationalStatus.waitingToLoad",
      {
        defaultValue:
          "A aguardar carga",
      }
    );
  }

  if (
    substatus ===
    "LOADING_IN_PROGRESS"
  ) {
    return t(
      "map.live.operationalStatus.loading",
      {
        defaultValue:
          "Carga em curso",
      }
    );
  }

  if (
    substatus ===
    "WAITING_TO_UNLOAD"
  ) {
    return t(
      "map.live.operationalStatus.waitingToUnload",
      {
        defaultValue:
          "A aguardar descarga",
      }
    );
  }

  if (
    substatus ===
    "UNLOADING_MERCHANDISE"
  ) {
    return t(
      "map.live.operationalStatus.unloading",
      {
        defaultValue:
          "Descarga em curso",
      }
    );
  }

  if (
    substatus === "DELAYED"
  ) {
    return t(
      "map.live.operationalStatus.delayed",
      {
        defaultValue:
          "Transporte atrasado",
      }
    );
  }

  return t(
    "map.live.operationalStatus.next",
    {
      location,

      defaultValue:
        "Próximo: {{location}}",
    }
  );
}

function formatUpdatedAt(
  value,
  locale
) {
  if (!value) return "";

  try {
    return new Date(
      value
    ).toLocaleTimeString(
      locale,
      {
        hour: "2-digit",
        minute: "2-digit",
      }
    );
  } catch {
    return "";
  }
}

function buildRoutesFeatureCollection(
  sessions,
  selectedSessionId
) {
  const selectedSession =
    sessions.find(
      (session) =>
        String(session.id) ===
        String(selectedSessionId)
    ) || null;

  if (
    !selectedSession ||
    selectedSession.geometry?.geometry?.type !==
      "LineString"
  ) {
    return {
      type: "FeatureCollection",
      features: [],
    };
  }

  return {
    type: "FeatureCollection",

    features: [
      {
        type: "Feature",

        geometry:
          selectedSession.geometry.geometry,

        properties: {
          sessionId:
            String(selectedSession.id),

          selected: true,

          status:
            selectedSession.status,
        },
      },
    ],
  };
}

function buildPointsFeatureCollection(
  sessions,
  selectedSessionId
) {
  const selectedSession =
    sessions.find(
      (session) =>
        String(session.id) ===
        String(selectedSessionId)
    ) || null;

  if (!selectedSession) {
    return {
      type: "FeatureCollection",
      features: [],
    };
  }

  const features =
    (selectedSession.points || [])
      .map((point) => {
        const lng = Number(point.lng);
        const lat = Number(point.lat);

        if (
          !Number.isFinite(lng) ||
          !Number.isFinite(lat) ||
          Math.abs(lng) > 180 ||
          Math.abs(lat) > 90
        ) {
          return null;
        }

        return {
          type: "Feature",

          geometry: {
            type: "Point",
            coordinates: [lng, lat],
          },

          properties: {
            sessionId: String(
              selectedSession.id
            ),
            pointId: String(point.id),
            type: point.type,
            label: getPointLabel(point),
            selected: true,
            showLabel: true,
            name: point.name || "",
          },
        };
      })
      .filter(Boolean);

  return {
    type: "FeatureCollection",
    features,
  };
}

const LiveTransportsMap = () => {
  const { t, i18n } =
    useTranslation("map");

  const locale =
    i18n.resolvedLanguage ||
    i18n.language ||
    "pt-PT";

  const mapContainerRef =
    useRef(null);

  const mapRef =
    useRef(null);

  const routeGeometryCacheRef =
    useRef(new Map());

  const routeGeometryFailureCacheRef =
    useRef(new Map());

  const liveSessionsCacheKeyRef =
    useRef(
      getLiveSessionsCacheKey()
    );

  const initialSessionsCacheRef =
    useRef(
      readLiveSessionsCache(
        liveSessionsCacheKeyRef
          .current
      )
    );

  const lastLoadsRevRef =
    useRef(
      initialSessionsCacheRef
        .current
        ?.loadsRev ??
      null
    );

  const socketRefreshTimerRef =
    useRef(null);

  const [viewMode, setViewMode] =
    useState("sessions");

  const [mapReady, setMapReady] =
    useState(false);

  const [sessions, setSessions] =
    useState(
      () =>
        initialSessionsCacheRef
          .current
          ?.sessions ||
        []
    );

  const [
    selectedSessionId,
    setSelectedSessionId,
  ] = useState(null);

  const [searchTerm, setSearchTerm] =
    useState("");

  const [
    isSidebarCollapsed,
    setIsSidebarCollapsed,
  ] = useState(false);

  const [loading, setLoading] =
    useState(
      () =>
        !initialSessionsCacheRef
          .current
    );

  const [refreshing, setRefreshing] =
    useState(false);

  const [error, setError] =
    useState(null);

  const [lastUpdated, setLastUpdated] =
    useState(
      () => {
        const cached =
          initialSessionsCacheRef
            .current
            ?.lastUpdated;

        return cached
          ? new Date(
              cached
            )
          : null;
      }
    );

  const fetchRouteGeometry =
    useCallback(
      async (
        sessionId,
        points
      ) => {
        if (points.length < 2) {
          return null;
        }

        const signature =
          points
            .map(
              (point) =>
                `${point.lat}:${point.lng}`
            )
            .join("|");

        const cacheKey =
          `${sessionId}:${signature}`;

        if (
          routeGeometryCacheRef.current.has(
            cacheKey
          )
        ) {
          return routeGeometryCacheRef.current.get(
            cacheKey
          );
        }

        const previousFailureAt =
          routeGeometryFailureCacheRef
            .current
            .get(
              cacheKey
            );

        if (
          Number.isFinite(
            Number(
              previousFailureAt
            )
          ) &&
          Date.now() -
            Number(
              previousFailureAt
            ) <
            15 * 1000
        ) {
          return null;
        }

        try {
          const { data } =
            await api.post(
              "/maps/route",
              {
                transportsessionId:
                  sessionId,

                costing:
                  "truck",

                alternatives:
                  0,

                includeTollEstimate:
                  false,

                validateLocations:
                  false,

                locations:
                  points.map(
                    (
                      point,
                      index
                    ) => ({
                      id:
                        point.id ||
                        `live-point-${index}`,

                      type:
                        point.type,

                      sequence:
                        index + 1,

                      lat:
                        point.lat,

                      lon:
                        point.lng,
                    })
                  ),
              }
            );

          const geometry =
            normalizeRouteResponse(
              data
            );

          if (geometry) {
            routeGeometryCacheRef.current.set(
              cacheKey,
              geometry
            );

            routeGeometryFailureCacheRef.current.delete(
              cacheKey
            );
          }

          return geometry;
        } catch (routeError) {
          routeGeometryFailureCacheRef.current.set(
            cacheKey,
            Date.now()
          );

          console.warn(
            "[LiveTransportsMap] Route calculation failed:",
            routeError?.message
          );

          return null;
        }
      },
      []
    );

  const refreshSessions =
    useCallback(
      async ({
        silent = false,
      } = {}) => {
        if (silent) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        setError(null);

        try {
          const { data: loadsData } =
            await api.get(
              "/loads/summary-with-progress",
              {
                params: {
                  view: "current",
                  withPoints: 0,
                  limit: 5000,
                },
              }
            );

          const loads =
            getArrayPayload(
              loadsData
            );

          const sessionIds =
            [
              ...new Set(
                loads
                  .map(
                    getTransportSessionId
                  )
                  .filter(Boolean)
              ),
            ];

          if (!sessionIds.length) {
            const now =
              new Date();

            setSessions(
              []
            );

            setSelectedSessionId(
              null
            );

            setLastUpdated(
              now
            );

            writeLiveSessionsCache(
              liveSessionsCacheKeyRef
                .current,
              {
                sessions:
                  [],

                lastUpdated:
                  now,

                loadsRev:
                  lastLoadsRevRef
                    .current,
              }
            );

            return;
          }

          const [
            pointsResponse,
            sessionResponses,
          ] = await Promise.all([
            api.get(
              "/pickupdeliveries/by-sessions",
              {
                params: {
                  ids:
                    sessionIds.join(
                      ","
                    ),
                },
              }
            ),

            Promise.allSettled(
              sessionIds.map(
                (sessionId) =>
                  api.get(
                    `/transportsessions/${sessionId}`
                  )
              )
            ),
          ]);

          const rawPoints =
            getArrayPayload(
              pointsResponse.data
            );

          const normalizedPoints =
            rawPoints
              .map(normalizePoint)
              .filter(Boolean);

          const pointsBySession =
            new Map();

          normalizedPoints.forEach(
            (point) => {
              const sessionId =
                point.transportsessionId;

              if (!sessionId) {
                return;
              }

              if (
                !pointsBySession.has(
                  sessionId
                )
              ) {
                pointsBySession.set(
                  sessionId,
                  []
                );
              }

              pointsBySession
                .get(sessionId)
                .push(point);
            }
          );

          const detailsBySession =
            new Map();

          sessionResponses.forEach(
            (
              response,
              index
            ) => {
              if (
                response.status !==
                "fulfilled"
              ) {
                return;
              }

              const raw =
                response.value.data
                  ?.transportSession ??
                response.value.data
                  ?.item ??
                response.value.data;

              detailsBySession.set(
                sessionIds[index],
                raw
              );
            }
          );

          const loadsBySession =
            new Map();

          loads.forEach((load) => {
            const sessionId =
              getTransportSessionId(
                load
              );

            if (!sessionId) {
              return;
            }

            if (
              !loadsBySession.has(
                sessionId
              )
            ) {
              loadsBySession.set(
                sessionId,
                []
              );
            }

            loadsBySession
              .get(sessionId)
              .push(load);
          });

          const builtSessions =
            await Promise.all(
              sessionIds.map(
                async (
                  sessionId
                ) => {
                  const detail =
                    detailsBySession.get(
                      sessionId
                    ) || {};

                  const sessionLoads =
                    loadsBySession.get(
                      sessionId
                    ) || [];

                  const status =
                    getSessionStatus(
                      detail
                    );

                  /*
                   * O endpoint de Loads já está scoped
                   * com view=current.
                   *
                   * Logo, a TS aparece se tiver pelo
                   * menos uma Current Load.
                   *
                   * Não usamos o status da própria TS
                   * como fonte de verdade para esta view.
                   */
                  if (
                    !sessionLoads.length
                  ) {
                    return null;
                  }

                  const routeSnapshot =
                    parseMaybeJson(
                      detail
                        ?.routeSnapshot
                    );

                  const operationalPoints =
                    (
                      pointsBySession.get(
                        sessionId
                      ) || []
                    )
                      .slice()
                      .sort(
                        (a, b) =>
                          a.sequence -
                          b.sequence
                      );

                  const snapshotPoints =
                    (
                      Array.isArray(
                        routeSnapshot
                          ?.points
                      )
                        ? routeSnapshot.points
                        : []
                    )
                      .map(
                        normalizeSnapshotPoint
                      )
                      .filter(Boolean)
                      .sort(
                        (a, b) =>
                          a.sequence -
                          b.sequence
                      );

                  /*
                   * Quando existe uma rota guardada,
                   * os seus pontos definem o trajeto.
                   *
                   * Os pontos operacionais continuam
                   * a ser apresentados no mapa e usados
                   * no cálculo do progresso, mas não
                   * devem substituir a rota guardada.
                   */
                  const routePoints =
                    snapshotPoints.length >= 2
                      ? snapshotPoints
                      : operationalPoints;

                  const displayPoints =
                    operationalPoints.length
                      ? operationalPoints
                      : routePoints;

                  const storedGeometry =
                    normalizeGeometry(
                      routeSnapshot
                        ?.route
                        ?.geometry
                    );

                  let geometry =
                    null;

                  if (
                    routePoints.length >= 2
                  ) {
                    geometry =
                      await fetchRouteGeometry(
                        sessionId,
                        routePoints
                      );
                  }

                  if (
                    !geometry
                  ) {
                    geometry =
                      storedGeometry;
                  }

                  const firstTruck =
                    operationalPoints.find(
                      (point) =>
                        point.truckPlate
                    )?.truckPlate ||
                    sessionLoads.find(
                      (load) =>
                        load?.truckPlate
                    )?.truckPlate ||
                    null;

                  const firstTrailer =
                    operationalPoints.find(
                      (point) =>
                        point.trailerPlate
                    )?.trailerPlate ||
                    detail
                      ?.trailerPlate ||
                    detail
                      ?.trailer
                      ?.trailerPlate ||
                    sessionLoads.find(
                      (load) =>
                        load?.trailerPlate
                    )?.trailerPlate ||
                    sessionLoads.find(
                      (load) =>
                        load?.trailer
                          ?.trailerPlate
                    )?.trailer
                      ?.trailerPlate ||
                    null;

                  const firstDriver =
                    operationalPoints.find(
                      (point) =>
                        point.driverName
                    )?.driverName ||
                    sessionLoads.find(
                      (load) =>
                        load?.driverName
                    )?.driverName ||
                    null;

                  return {
                    id:
                      sessionId,

                    transportSessionNumber:
                      detail
                        ?.transportSessionNumber ??
                      detail
                        ?.transportsessionNumber ??
                      sessionLoads[0]
                        ?.transportSessionNumber ??
                      null,

                    status,

                    routeName:
                      routeSnapshot
                        ?.name ||
                      null,

                    routeNumber:
                      routeSnapshot
                        ?.routeNumber ||
                      null,

                    routeSnapshot,

                    routeTemplateId:
                      detail
                        ?.routeTemplateId ??
                      routeSnapshot
                        ?.templateId ??
                      null,

                    routeTemplateVersion:
                      detail
                        ?.routeTemplateVersion ??
                      routeSnapshot
                        ?.templateVersion ??
                      null,

                    truckPlate:
                      firstTruck,

                    trailerPlate:
                      firstTrailer,

                    driverName:
                      firstDriver,

                    points:
                      displayPoints,

                    operationalPoints,

                    loads:
                      sessionLoads,

                    progress:
                      calculateProgress(
                        operationalPoints
                      ),

                    geometry,

                    updatedAt:
                      detail
                        ?.updatedAt ||
                      sessionLoads[0]
                        ?.updatedAt ||
                      null,
                  };
                }
              )
            );

          const activeSessions =
            builtSessions
              .filter(Boolean)
              .sort((a, b) => {
                const numberA =
                  Number(
                    a.transportSessionNumber ||
                    a.id
                  );

                const numberB =
                  Number(
                    b.transportSessionNumber ||
                    b.id
                  );

                return (
                  numberB -
                  numberA
                );
              });

          const now =
            new Date();

          setSessions(
            activeSessions
          );

          setSelectedSessionId(
            (current) => {
              const stillExists =
                activeSessions.some(
                  (session) =>
                    String(
                      session.id
                    ) ===
                    String(
                      current
                    )
                );

              if (
                stillExists
              ) {
                return current;
              }

              return (
                activeSessions[0]
                  ?.id ||
                null
              );
            }
          );

          setLastUpdated(
            now
          );

          writeLiveSessionsCache(
            liveSessionsCacheKeyRef
              .current,
            {
              sessions:
                activeSessions,

              lastUpdated:
                now,

              loadsRev:
                lastLoadsRevRef
                  .current,
            }
          );
        } catch (loadError) {
          console.error(
            "[LiveTransportsMap] Failed to load sessions:",
            loadError
          );

          setError(
            loadError?.response?.data
              ?.error ||
            t(
              "map.live.errors.load",
              {
                defaultValue:
                  "Não foi possível carregar os transportes atuais.",
              }
            )
          );
        } finally {
          setLoading(false);
          setRefreshing(false);
        }
      },
      [
        fetchRouteGeometry,
        t,
      ]
    );

    const focusSessionOnMap =
    useCallback(
      (sessionId) => {
        if (
          !mapReady ||
          !mapRef.current
        ) {
          return;
        }

        const session =
          sessions.find(
            (item) =>
              String(item.id) ===
              String(sessionId)
          );

        if (!session) {
          return;
        }

        const map =
          mapRef.current;

        const bounds =
          new maplibregl.LngLatBounds();

        let coordinateCount = 0;

        const geometryCoordinates =
          session.geometry
            ?.geometry
            ?.coordinates;

        /*
         * A geometry da rota é prioritária.
         * É esta que representa realmente
         * o trajeto por estrada.
         */
        if (
          Array.isArray(
            geometryCoordinates
          )
        ) {
          geometryCoordinates.forEach(
            (coordinate) => {
              const lng =
                Number(
                  coordinate?.[0]
                );

              const lat =
                Number(
                  coordinate?.[1]
                );

              if (
                !Number.isFinite(lng) ||
                !Number.isFinite(lat) ||
                Math.abs(lng) > 180 ||
                Math.abs(lat) > 90
              ) {
                return;
              }

              bounds.extend([
                lng,
                lat,
              ]);

              coordinateCount += 1;
            }
          );
        }

        /*
         * Só usamos os pontos quando não
         * existe geometry válida.
         */
        if (!coordinateCount) {
          session.points.forEach(
            (point) => {
              const lng =
                Number(point.lng);

              const lat =
                Number(point.lat);

              if (
                !Number.isFinite(lng) ||
                !Number.isFinite(lat) ||
                Math.abs(lng) > 180 ||
                Math.abs(lat) > 90
              ) {
                return;
              }

              bounds.extend([
                lng,
                lat,
              ]);

              coordinateCount += 1;
            }
          );
        }

        if (!coordinateCount) {
          return;
        }

        try {
          map.stop();
          map.resize();
        } catch {}

        if (coordinateCount === 1) {
          map.easeTo({
            center:
              bounds.getCenter(),

            zoom:
              11,

            duration:
              350,
          });

          return;
        }

        map.fitBounds(
          bounds,
          {
            padding: {
              top: 90,
              right: 90,
              bottom: 90,
              left: 90,
            },

            maxZoom:
              12,

            duration:
              450,
          }
        );
      },
      [
        mapReady,
        sessions,
      ]
    );

  useEffect(() => {
    if (
      viewMode !==
        "sessions" ||
      !mapReady ||
      !selectedSessionId
    ) {
      return;
    }

    window.requestAnimationFrame(
      () => {
        focusSessionOnMap(
          selectedSessionId
        );
      }
    );
  }, [
    focusSessionOnMap,
    mapReady,
    selectedSessionId,
    viewMode,
  ]);

  useEffect(() => {
    if (
      viewMode !==
      "sessions"
    ) {
      return undefined;
    }

    const hasCache =
      Boolean(
        initialSessionsCacheRef
          .current
      );

    refreshSessions({
      silent:
        hasCache,
    });

    return undefined;
  }, [
    refreshSessions,
    viewMode,
  ]);

  const liveSessionIdsKey =
    useMemo(
      () =>
        sessions
          .map(
            (
              session
            ) =>
              Number(
                session.id
              )
          )
          .filter(
            (
              sessionId
            ) =>
              Number.isInteger(
                sessionId
              ) &&
              sessionId > 0
          )
          .sort(
            (
              first,
              second
            ) =>
              first -
              second
          )
          .join(
            ","
          ),
      [
        sessions,
      ]
    );

  useEffect(() => {
    const socket =
      getSocket();

    const sessionIds =
      liveSessionIdsKey
        ? liveSessionIdsKey
            .split(",")
            .map(
              Number
            )
            .filter(
              Number.isInteger
            )
        : [];

    const scheduleRefresh =
      () => {
        if (
          socketRefreshTimerRef
            .current
        ) {
          window.clearTimeout(
            socketRefreshTimerRef
              .current
          );
        }

        /*
         * Um único update no backend pode
         * disparar vários eventos quase
         * ao mesmo tempo.
         *
         * Agrupamos tudo num único refresh.
         */
        socketRefreshTimerRef.current =
          window.setTimeout(
            () => {
              socketRefreshTimerRef.current =
                null;

              refreshSessions({
                silent:
                  true,
              });
            },
            120
          );
      };

    const subscribe =
      () => {
        if (
          !socket.connected
        ) {
          return;
        }

        socket.emit(
          "subscribe:loads"
        );

        if (
          sessionIds.length
        ) {
          socket.emit(
            "subscribe:sessions",
            {
              ids:
                sessionIds,
            }
          );
        }
      };

    const handleConnect =
      () => {
        subscribe();

        /*
         * Podemos ter perdido eventos
         * enquanto o socket esteve offline.
         */
        scheduleRefresh();
      };

    const handleLoadsRev =
      (
        payload
      ) => {
        const nextRev =
          Number(
            payload?.loadsRev
          );

        if (
          Number.isFinite(
            nextRev
          )
        ) {
          const previousRev =
            Number(
              lastLoadsRevRef
                .current
            );

          if (
            Number.isFinite(
              previousRev
            ) &&
            nextRev <=
              previousRev
          ) {
            return;
          }

          lastLoadsRevRef.current =
            nextRev;
        }

        scheduleRefresh();
      };

    const handleSessionProgress =
      (
        payload
      ) => {
        const sessionId =
          Number(
            payload?.sessionId ??
            payload?.transportsessionId
          );

        const pct =
          Number(
            payload?.pct
          );

        if (
          Number.isInteger(
            sessionId
          ) &&
          Number.isFinite(
            pct
          )
        ) {
          setSessions(
            (
              current
            ) =>
              current.map(
                (
                  session
                ) =>
                  Number(
                    session.id
                  ) ===
                  sessionId
                    ? {
                        ...session,

                        progress:
                          Math.max(
                            0,
                            Math.min(
                              100,
                              Math.round(
                                pct
                              )
                            )
                          ),
                      }
                    : session
              )
          );
        }

        scheduleRefresh();
      };

    const handlePickupDeliveryUpdated =
      (
        payload
      ) => {
        const pointId =
          payload?.id ??
          payload?.pickupDeliveryId ??
          null;

        if (
          pointId !==
            null &&
          payload?.status
        ) {
          setSessions(
            (
              current
            ) =>
              current.map(
                (
                  session
                ) => {
                  const updatePoints =
                    (
                      points
                    ) =>
                      (
                        points ||
                        []
                      ).map(
                        (
                          point
                        ) =>
                          String(
                            point.id
                          ) ===
                          String(
                            pointId
                          )
                            ? {
                                ...point,

                                status:
                                  String(
                                    payload.status
                                  )
                                    .trim()
                                    .toUpperCase(),
                              }
                            : point
                      );

                  const nextOperationalPoints =
                    updatePoints(
                      session
                        .operationalPoints
                    );

                  return {
                    ...session,

                    operationalPoints:
                      nextOperationalPoints,

                    points:
                      updatePoints(
                        session.points
                      ),

                    progress:
                      calculateProgress(
                        nextOperationalPoints
                      ),
                  };
                }
              )
          );
        }

        scheduleRefresh();
      };

    const genericEvents = [
      "load:created",
      "load:updated",
      "load:deleted",
      "loads:stale",
      "load:points:stale",
      "session:points",
      "session:points:stale",
      "session:route:updated",
      "pd:created",
      "pd:deleted",
      "pd:bulk-driver",
      "pd:bulk-truck",
      "pd:bulk-trailer",
    ];

    socket.on(
      "connect",
      handleConnect
    );

    socket.on(
      "reconnect",
      handleConnect
    );

    socket.on(
      "loads:rev",
      handleLoadsRev
    );

    socket.on(
      "session:progress",
      handleSessionProgress
    );

    socket.on(
      "pd:updated",
      handlePickupDeliveryUpdated
    );

    genericEvents.forEach(
      (
        eventName
      ) => {
        socket.on(
          eventName,
          scheduleRefresh
        );
      }
    );

    if (
      socket.connected
    ) {
      subscribe();
    } else if (
      typeof socket.connect ===
      "function"
    ) {
      socket.connect();
    }

    const handleOnline =
      () => {
        scheduleRefresh();
      };

    const handleVisibilityChange =
      () => {
        if (
          document.visibilityState ===
          "visible"
        ) {
          scheduleRefresh();
        }
      };

    window.addEventListener(
      "online",
      handleOnline
    );

    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange
    );

    /*
     * Safety net:
     *
     * Com WebSocket ligado:
     * zero polling.
     *
     * Se o socket estiver desligado:
     * valida silenciosamente a cada 5 min.
     */
    const safetyInterval =
      window.setInterval(
        () => {
          if (
            !socket.connected
          ) {
            refreshSessions({
              silent:
                true,
            });
          }
        },
        5 * 60 * 1000
      );

    return () => {
      if (
        socketRefreshTimerRef
          .current
      ) {
        window.clearTimeout(
          socketRefreshTimerRef
            .current
        );

        socketRefreshTimerRef.current =
          null;
      }

      window.clearInterval(
        safetyInterval
      );

      socket.off(
        "connect",
        handleConnect
      );

      socket.off(
        "reconnect",
        handleConnect
      );

      socket.off(
        "loads:rev",
        handleLoadsRev
      );

      socket.off(
        "session:progress",
        handleSessionProgress
      );

      socket.off(
        "pd:updated",
        handlePickupDeliveryUpdated
      );

      genericEvents.forEach(
        (
          eventName
        ) => {
          socket.off(
            eventName,
            scheduleRefresh
          );
        }
      );

      window.removeEventListener(
        "online",
        handleOnline
      );

      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );
    };
  }, [
    liveSessionIdsKey,
    refreshSessions,
  ]);

  useEffect(() => {
    if (
      viewMode !==
        "sessions" ||
      !mapContainerRef.current
    ) {
      return undefined;
    }

    let resizeObserver =
      null;

    let resizeFrame =
      null;

    const map =
      new maplibregl.Map({
        container:
          mapContainerRef.current,

        style:
          MAP_STYLE,

        center:
          DEFAULT_CENTER,

        zoom:
          DEFAULT_ZOOM,

        attributionControl:
          true,
      });

    mapRef.current = map;

    map.scrollZoom.enable();
    map.boxZoom.enable();
    map.dragPan.enable();
    map.keyboard.enable();
    map.doubleClickZoom.enable();
    map.touchZoomRotate.enable();

    map.addControl(
      new maplibregl.NavigationControl(),
      "top-right"
    );

    const resizeMap = () => {
      if (resizeFrame) {
        window.cancelAnimationFrame(
          resizeFrame
        );
      }

      resizeFrame =
        window.requestAnimationFrame(
          () => {
            try {
              map.resize();
            } catch {}
          }
        );
    };

    if (
      typeof ResizeObserver !==
      "undefined"
    ) {
      resizeObserver =
        new ResizeObserver(
          resizeMap
        );

      resizeObserver.observe(
        mapContainerRef.current
      );
    }

    window.addEventListener(
      "resize",
      resizeMap
    );

    const handleRouteClick =
      (event) => {
        const sessionId =
          event?.features?.[0]
            ?.properties
            ?.sessionId;

        if (sessionId) {
          setSelectedSessionId(
            Number(sessionId)
          );
        }
      };

    const handlePointClick =
      (event) => {
        const sessionId =
          event?.features?.[0]
            ?.properties
            ?.sessionId;

        if (sessionId) {
          setSelectedSessionId(
            Number(sessionId)
          );
        }
      };
    
    const handleClusterClick =
      async (event) => {
        const feature =
          event?.features?.[0];

        const clusterId =
          feature?.properties
            ?.cluster_id;

        const coordinates =
          feature?.geometry
            ?.coordinates;

        if (
          clusterId === null ||
          clusterId === undefined ||
          !Array.isArray(
            coordinates
          )
        ) {
          return;
        }

        const source =
          map.getSource(
            POINTS_SOURCE_ID
          );

        if (
          !source ||
          typeof source
            .getClusterExpansionZoom !==
            "function"
        ) {
          return;
        }

        try {
          const zoom =
            await source
              .getClusterExpansionZoom(
                clusterId
              );

          map.easeTo({
            center:
              coordinates,

            zoom:
              Math.min(
                Number(zoom) || 12,
                15
              ),

            duration:
              350,
          });
        } catch (clusterError) {
          console.warn(
            "[LiveTransportsMap] Could not expand cluster:",
            clusterError?.message
          );
        }
      };

    const handlePointerEnter =
      () => {
        map.getCanvas().style.cursor =
          "pointer";
      };

    const handlePointerLeave =
      () => {
        map.getCanvas().style.cursor =
          "";
      };

    const handleLoad = () => {
      map.addSource(
        ROUTES_SOURCE_ID,
        {
          type: "geojson",

          data: {
            type:
              "FeatureCollection",

            features: [],
          },
        }
      );

      map.addLayer({
        id:
          ROUTES_CASING_LAYER_ID,

        type:
          "line",

        source:
          ROUTES_SOURCE_ID,

        layout: {
          "line-cap":
            "round",

          "line-join":
            "round",
        },

        paint: {
          "line-color":
            "#ffffff",

          "line-width": [
            "case",

            [
              "boolean",
              ["get", "selected"],
              false,
            ],

            10,
            7,
          ],

          "line-opacity":
            0.94,
        },
      });

      map.addLayer({
        id:
          ROUTES_LINE_LAYER_ID,

        type:
          "line",

        source:
          ROUTES_SOURCE_ID,

        layout: {
          "line-cap":
            "round",

          "line-join":
            "round",
        },

        paint: {
          "line-color": [
            "case",

            [
              "boolean",
              ["get", "selected"],
              false,
            ],

            "#2563eb",
            "#64748b",
          ],

          "line-width": [
            "case",

            [
              "boolean",
              ["get", "selected"],
              false,
            ],

            6,
            3.5,
          ],

          "line-opacity": [
            "case",

            [
              "boolean",
              ["get", "selected"],
              false,
            ],

            1,
            0.55,
          ],
        },
      });

      try {
        ensureRoadReferenceOverlay(
          map
        );
      } catch (
        roadReferenceError
      ) {
        console.warn(
          "[LiveTransportsMap] Could not initialise road references:",
          roadReferenceError
        );
      }

      map.addSource(
        POINTS_SOURCE_ID,
        {
          type: "geojson",

          cluster: true,

          clusterMaxZoom:
            14,

          clusterRadius:
            42,

          data: {
            type:
              "FeatureCollection",

            features: [],
          },
        }
      );

      map.addLayer({
        id:
          POINTS_CLUSTER_LAYER_ID,

        type:
          "circle",

        source:
          POINTS_SOURCE_ID,

        filter: [
          "has",
          "point_count",
        ],

        paint: {
          "circle-radius": [
            "step",

            [
              "get",
              "point_count",
            ],

            18,

            5,
            21,

            10,
            24,

            25,
            28,
          ],

          "circle-color": [
            "step",

            [
              "get",
              "point_count",
            ],

            "#0b8dbd",

            5,
            "#2563eb",

            10,
            "#1d4ed8",

            25,
            "#1e3a8a",
          ],

          "circle-stroke-color":
            "#ffffff",

          "circle-stroke-width":
            3,

          "circle-opacity":
            0.96,
        },
      });

      map.addLayer({
        id:
          POINTS_CLUSTER_COUNT_LAYER_ID,

        type:
          "symbol",

        source:
          POINTS_SOURCE_ID,

        filter: [
          "has",
          "point_count",
        ],

        layout: {
          "text-field": [
            "get",
            "point_count_abbreviated",
          ],

          "text-size":
            12,

          "text-allow-overlap":
            true,

          "text-ignore-placement":
            true,
        },

        paint: {
          "text-color":
            "#ffffff",

          "text-halo-color":
            "rgba(15, 23, 42, 0.12)",

          "text-halo-width":
            0.5,
        },
      });

      map.addLayer({
        id:
          POINTS_HALO_LAYER_ID,

        type:
          "circle",

        source:
          POINTS_SOURCE_ID,

        filter: [
          "!",
          [
            "has",
            "point_count",
          ],
        ],

        paint: {
          "circle-radius": [
            "case",

            [
              "boolean",
              ["get", "selected"],
              false,
            ],

            16,
            9,
          ],

          "circle-color":
            "#ffffff",

          "circle-opacity":
            0.96,

          "circle-stroke-color":
            "rgba(15, 23, 42, 0.14)",

          "circle-stroke-width":
            1,
        },
      });

      map.addLayer({
        id:
          POINTS_CIRCLE_LAYER_ID,

        type:
          "circle",

        source:
          POINTS_SOURCE_ID,

        filter: [
          "!",
          [
            "has",
            "point_count",
          ],
        ],

        paint: {
          "circle-radius": [
            "case",

            [
              "boolean",
              ["get", "selected"],
              false,
            ],

            12,
            6,
          ],

          "circle-color": [
            "match",

            ["get", "type"],

            "PICKUP",
            "#0b8dbd",

            "ORIGIN",
            "#0b8dbd",

            "DELIVERY",
            "#111827",

            "DESTINATION",
            "#111827",

            "FUEL",
            "#f59e0b",

            "STRATEGICPOINT",
            "#9333ea",

            "VIA",
            "#9333ea",

            "#64748b",
          ],

          "circle-stroke-color":
            "#ffffff",

          "circle-stroke-width":
            2,
        },
      });

      map.addLayer({
        id:
          POINTS_LABEL_LAYER_ID,

        type:
          "symbol",

        source:
          POINTS_SOURCE_ID,

        filter: [
          "all",

          [
            "!",
            [
              "has",
              "point_count",
            ],
          ],

          [
            "==",
            [
              "get",
              "showLabel",
            ],
            true,
          ],
        ],

        layout: {
          "text-field":
            ["get", "label"],

          "text-size":
            10,

          "text-allow-overlap":
            true,

          "text-ignore-placement":
            true,
        },

        paint: {
          "text-color":
            "#ffffff",

          "text-halo-color":
            "rgba(15, 23, 42, 0.08)",

          "text-halo-width":
            0.4,
        },
      });

      map.on(
        "click",
        ROUTES_LINE_LAYER_ID,
        handleRouteClick
      );

      map.on(
        "click",
        POINTS_CLUSTER_LAYER_ID,
        handleClusterClick
      );

      map.on(
        "click",
        POINTS_CIRCLE_LAYER_ID,
        handlePointClick
      );

      map.on(
        "mouseenter",
        ROUTES_LINE_LAYER_ID,
        handlePointerEnter
      );

      map.on(
        "mouseleave",
        ROUTES_LINE_LAYER_ID,
        handlePointerLeave
      );

      map.on(
        "mouseenter",
        POINTS_CLUSTER_LAYER_ID,
        handlePointerEnter
      );

      map.on(
        "mouseleave",
        POINTS_CLUSTER_LAYER_ID,
        handlePointerLeave
      );

      map.on(
        "mouseenter",
        POINTS_CIRCLE_LAYER_ID,
        handlePointerEnter
      );

      map.on(
        "mouseleave",
        POINTS_CIRCLE_LAYER_ID,
        handlePointerLeave
      );

      resizeMap();

      window.setTimeout(
        resizeMap,
        100
      );

      setMapReady(true);
    };

    map.on(
      "load",
      handleLoad
    );

    return () => {
      setMapReady(false);

      resizeObserver?.disconnect();

      window.removeEventListener(
        "resize",
        resizeMap
      );

      if (resizeFrame) {
        window.cancelAnimationFrame(
          resizeFrame
        );
      }

      try {
        map.off(
          "load",
          handleLoad
        );

        if (
          map.getLayer(
            ROUTES_LINE_LAYER_ID
          )
        ) {
          map.off(
            "click",
            ROUTES_LINE_LAYER_ID,
            handleRouteClick
          );

          map.off(
            "mouseenter",
            ROUTES_LINE_LAYER_ID,
            handlePointerEnter
          );

          map.off(
            "mouseleave",
            ROUTES_LINE_LAYER_ID,
            handlePointerLeave
          );
        }

        if (
          map.getLayer(
            POINTS_CLUSTER_LAYER_ID
          )
        ) {
          map.off(
            "click",
            POINTS_CLUSTER_LAYER_ID,
            handleClusterClick
          );

          map.off(
            "mouseenter",
            POINTS_CLUSTER_LAYER_ID,
            handlePointerEnter
          );

          map.off(
            "mouseleave",
            POINTS_CLUSTER_LAYER_ID,
            handlePointerLeave
          );
        }

        if (
          map.getLayer(
            POINTS_CIRCLE_LAYER_ID
          )
        ) {
          map.off(
            "click",
            POINTS_CIRCLE_LAYER_ID,
            handlePointClick
          );

          map.off(
            "mouseenter",
            POINTS_CIRCLE_LAYER_ID,
            handlePointerEnter
          );

          map.off(
            "mouseleave",
            POINTS_CIRCLE_LAYER_ID,
            handlePointerLeave
          );
        }

        map.remove();
      } catch {}

      if (
        mapRef.current === map
      ) {
        mapRef.current = null;
      }
    };
  }, [
    viewMode,
  ]);

  useEffect(() => {
    if (
      viewMode !==
        "sessions" ||
      !mapReady ||
      !mapRef.current
    ) {
      return;
    }

    const map =
      mapRef.current;

    const routesSource =
      map.getSource(
        ROUTES_SOURCE_ID
      );

    const pointsSource =
      map.getSource(
        POINTS_SOURCE_ID
      );

    routesSource?.setData?.(
      buildRoutesFeatureCollection(
        sessions,
        selectedSessionId
      )
    );

    pointsSource?.setData?.(
      buildPointsFeatureCollection(
        sessions,
        selectedSessionId
      )
    );
  }, [
    mapReady,
    selectedSessionId,
    sessions,
    viewMode,
  ]);

  useEffect(() => {
    let secondFrame = null;

    const resizeMap = () => {
      try {
        mapRef.current?.resize();
      } catch {}
    };

    const firstFrame =
      window.requestAnimationFrame(
        () => {
          resizeMap();

          secondFrame =
            window.requestAnimationFrame(
              resizeMap
            );
        }
      );

    const timer =
      window.setTimeout(
        resizeMap,
        60
      );

    return () => {
      window.cancelAnimationFrame(
        firstFrame
      );

      if (secondFrame) {
        window.cancelAnimationFrame(
          secondFrame
        );
      }

      window.clearTimeout(
        timer
      );
    };
  }, [isSidebarCollapsed]);

  const filteredSessions =
    useMemo(() => {
      const cleanSearch =
        searchTerm
          .trim()
          .toLowerCase();

      if (!cleanSearch) {
        return sessions;
      }

      return sessions.filter(
        (session) => {
          const values = [
            getSessionDisplayNumber(
              session
            ),

            session.routeName,

            session.truckPlate,

            session.trailerPlate,

            session.driverName,

            session.status,

            ...session.points.map(
              (point) =>
                [
                  point.name,
                  point.city,
                  point.country,
                  point.address,
                ]
                  .filter(Boolean)
                  .join(" ")
            ),
          ];

          return values.some(
            (value) =>
              String(value || "")
                .toLowerCase()
                .includes(
                  cleanSearch
                )
          );
        }
      );
    }, [
      searchTerm,
      sessions,
    ]);

  const selectedSession =
    useMemo(() => {
      return sessions.find(
        (session) =>
          String(session.id) ===
          String(
            selectedSessionId
          )
      ) || null;
    }, [
      selectedSessionId,
      sessions,
    ]);

  const getStatusLabel =
    (status) => {
      return t(
        `map.live.statuses.${status}`,
        {
          defaultValue:
            status,
        }
      );
    };

  if (
    viewMode ===
    "drivers"
  ) {
    return (
    <DriverRoutePlannerMap
      currentSessions={
        sessions
      }
      currentSessionsLoading={
        loading
      }
      onRouteSaved={() =>
        refreshSessions({
          silent:
            true,
        })
      }
      onShowSessions={() =>
        setViewMode(
          "sessions"
        )
      }
    />
    );
  }

  return (
    <div
      className={[
        "map-view",
        "live-transports-map",
        isSidebarCollapsed
          ? "is-sidebar-collapsed"
          : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <aside className="map-view__sidebar live-transports-map__sidebar">
        <div className="live-transports-map__header">
          <div>
            <h2 className="map-view__sidebar-title">
              {t(
                "map.live.title",
                {
                  defaultValue:
                    "Transportes atuais",
                }
              )}
            </h2>

            <p className="map-view__sidebar-description">
              {t(
                "map.live.description",
                {
                  defaultValue:
                    "Consulte as sessões de transporte atualmente em operação.",
                }
              )}
            </p>
          </div>

          <button
            type="button"
            className="live-transports-map__refresh"
            onClick={() =>
              refreshSessions({
                silent: true,
              })
            }
            disabled={
              refreshing ||
              loading
            }
            aria-label={t(
              "map.live.refresh",
              {
                defaultValue:
                  "Atualizar transportes",
              }
            )}
          >
            <RefreshCw
              size={17}
              className={
                refreshing
                  ? "is-spinning"
                  : ""
              }
            />
          </button>
        </div>

        <div
          className="live-map-mode-toggle"
          role="tablist"
          aria-label="Modo do mapa"
        >
          <button
            type="button"
            className="is-active"
          >
            Sessões
          </button>

          <button
            type="button"
            onClick={() =>
              setViewMode(
                "drivers"
              )
            }
          >
            Motoristas
          </button>
        </div>

        <label className="live-transports-map__search">
          <Search
            size={17}
            aria-hidden="true"
          />

          <input
            type="search"
            value={searchTerm}
            onChange={(event) =>
              setSearchTerm(
                event.target.value
              )
            }
            placeholder={t(
              "map.live.searchPlaceholder",
              {
                defaultValue:
                  "Pesquisar transportes...",
              }
            )}
          />
        </label>

        {lastUpdated && (
          <div className="live-transports-map__updated">
            <Clock3
              size={14}
              aria-hidden="true"
            />

            <span>
              {t(
                "map.live.updatedAt",
                {
                  time:
                    formatUpdatedAt(
                      lastUpdated,
                      locale
                    ),

                  defaultValue:
                    "Atualizado às {{time}}",
                }
              )}
            </span>
          </div>
        )}

        {error && (
          <div className="live-transports-map__error">
            <AlertCircle
              size={18}
              aria-hidden="true"
            />

            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="live-transports-map__loading">
            <RefreshCw
              size={21}
              className="is-spinning"
            />

            <span>
              {t(
                "map.live.loading",
                {
                  defaultValue:
                    "A carregar transportes...",
                }
              )}
            </span>
          </div>
        ) : filteredSessions.length ? (
          <div className="live-transports-map__list">
            {filteredSessions.map(
              (session) => {
                const selected =
                  String(
                    session.id
                  ) ===
                  String(
                    selectedSessionId
                  );

                const cardPoints =
                  session
                    .operationalPoints
                    ?.length
                    ? session
                        .operationalPoints
                    : session.points;

                const completedPointCount =
                  calculateCompletedPointCount(
                    cardPoints
                  );

                const nextPoint =
                  getNextOperationalPoint(
                    cardPoints
                  );

                const currentPointNumber =
                  cardPoints.length
                    ? Math.min(
                        completedPointCount +
                          1,
                        cardPoints.length
                      )
                    : 0;

                const nextPointLocation =
                  getPointLocationMeta(
                    nextPoint
                  );

                const nextPointDateTime =
                  formatPointDateTime(
                    nextPoint,
                    locale
                  );

                const nextPointFlag =
                  getCountryFlag(
                    nextPoint
                  );

                const operationalStatus =
                  getOperationalStatusText(
                    nextPoint,
                    t
                  );

                return (
                  <button
                    key={
                      session.id
                    }
                    type="button"
                    className={[
                      "live-transport-card",

                      selected
                        ? "is-selected"
                        : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() => {
                      const isAlreadySelected =
                        String(
                          selectedSessionId
                        ) ===
                        String(
                          session.id
                        );

                      if (
                        isAlreadySelected
                      ) {
                        window.requestAnimationFrame(
                          () => {
                            focusSessionOnMap(
                              session.id
                            );
                          }
                        );

                        return;
                      }

                      setSelectedSessionId(
                        session.id
                      );

                      window.requestAnimationFrame(
                        () => {
                          focusSessionOnMap(
                            session.id
                          );
                        }
                      );
                    }}
                  >
                    <div className="live-transport-card__top">
                      <div>
                        <span className="live-transport-card__number">
                          {getSessionDisplayNumber(
                            session
                          )}
                        </span>

                        <strong>
                          {session.routeName ||
                            t(
                              "map.live.withoutSavedRoute",
                              {
                                defaultValue:
                                  "Rota operacional",
                              }
                            )}
                        </strong>
                      </div>

                      <span
                        className={`live-transport-card__status is-${session.status.toLowerCase()}`}
                      >
                        {getStatusLabel(
                          session.status
                        )}
                      </span>
                    </div>

                    {operationalStatus && (
                      <div className="live-transport-card__operational-status">
                        <span
                          className="live-transport-card__operational-dot"
                          aria-hidden="true"
                        />

                        <span>
                          {operationalStatus}
                        </span>
                      </div>
                    )}

                    <div className="live-transport-card__resources">
                      <div className="live-transport-card__resource">
                        <Truck
                          size={15}
                        />

                        <div className="live-transport-card__resource-copy">
                          <span className="live-transport-card__resource-label">
                            {t(
                              "map.live.truck",
                              {
                                defaultValue:
                                  "Camião",
                              }
                            )}
                          </span>

                          <span className="live-transport-card__resource-value">
                            {session.truckPlate ||
                              t(
                                "map.live.unassigned",
                                {
                                  defaultValue:
                                    "Não atribuído",
                                }
                              )}
                          </span>
                        </div>
                      </div>

                      <div className="live-transport-card__resource">
                        <Container
                          size={15}
                        />

                        <div className="live-transport-card__resource-copy">
                          <span className="live-transport-card__resource-label">
                            {t(
                              "map.live.trailer",
                              {
                                defaultValue:
                                  "Reboque",
                              }
                            )}
                          </span>

                          <span className="live-transport-card__resource-value">
                            {session.trailerPlate ||
                              t(
                                "map.live.unassigned",
                                {
                                  defaultValue:
                                    "Não atribuído",
                                }
                              )}
                          </span>
                        </div>
                      </div>

                      <div className="live-transport-card__resource">
                        <User
                          size={15}
                        />

                        <div className="live-transport-card__resource-copy">
                          <span className="live-transport-card__resource-label">
                            {t(
                              "map.live.driver",
                              {
                                defaultValue:
                                  "Motorista",
                              }
                            )}
                          </span>

                          <span className="live-transport-card__resource-value">
                            {session.driverName ||
                              t(
                                "map.live.unassigned",
                                {
                                  defaultValue:
                                    "Não atribuído",
                                }
                              )}
                          </span>
                        </div>
                      </div>

                      <div className="live-transport-card__resource">
                        <MapPin
                          size={15}
                        />

                        <div className="live-transport-card__resource-copy">
                          <span className="live-transport-card__resource-label">
                            {t(
                              "map.live.points",
                              {
                                defaultValue:
                                  "Pontos",
                              }
                            )}
                          </span>

                          <span className="live-transport-card__resource-value">
                            {cardPoints.length
                              ? t(
                                  "map.live.pointPosition",
                                  {
                                    current:
                                      currentPointNumber,

                                    total:
                                      cardPoints.length,

                                    defaultValue:
                                      "Ponto {{current}} de {{total}}",
                                  }
                                )
                              : t(
                                  "map.live.pointCount",
                                  {
                                    count:
                                      0,

                                    defaultValue:
                                      "{{count}} pontos",
                                  }
                                )}
                          </span>
                        </div>
                      </div>
                    </div>

                    {nextPoint && (
                      <div className="live-transport-card__next-point">
                        <div className="live-transport-card__next-heading">
                          <MapPin
                            size={15}
                          />

                          <span>
                            {t(
                              "map.live.nextPoint",
                              {
                                defaultValue:
                                  "Próximo ponto",
                              }
                            )}
                          </span>
                        </div>

                        <strong className="live-transport-card__next-name">
                          {nextPoint.name ||
                            t(
                              "map.live.withoutLocation",
                              {
                                defaultValue:
                                  "Localização não definida",
                              }
                            )}
                        </strong>

                        <div className="live-transport-card__next-meta">
                          {nextPointLocation && (
                            <span>
                              {nextPointFlag && (
                                <span
                                  className="live-transport-card__flag"
                                  aria-hidden="true"
                                >
                                  {nextPointFlag}
                                </span>
                              )}

                              <span>
                                {nextPointLocation}
                              </span>
                            </span>
                          )}

                          {nextPointDateTime && (
                            <span>
                              <Clock3
                                size={15}
                              />

                              <span>
                                {nextPointDateTime}
                              </span>
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="live-transport-card__progress-row">
                      <span>
                        {t(
                          "map.live.progress",
                          {
                            defaultValue:
                              "Progresso",
                          }
                        )}
                      </span>

                      <strong>
                        {session.progress}%
                      </strong>
                    </div>

                    <div className="live-transport-card__progress">
                      <span
                        style={{
                          width:
                            `${session.progress}%`,
                        }}
                      />
                    </div>

                    {!!cardPoints.length && (
                      <div className="live-transport-card__completed">
                        {t(
                          "map.live.completedPoints",
                          {
                            completed:
                              completedPointCount,

                            total:
                              cardPoints.length,

                            defaultValue:
                              "{{completed}} de {{total}} pontos concluídos",
                          }
                        )}
                      </div>
                    )}
                  </button>
                );
              }
            )}
          </div>
        ) : (
          <div className="map-view__sidebar-empty">
            <div className="map-view__sidebar-empty-icon">
              <Route />
            </div>

            <strong>
              {searchTerm
                ? t(
                    "map.live.noResultsTitle",
                    {
                      defaultValue:
                        "Nenhum transporte encontrado",
                    }
                  )
                : t(
                    "map.live.emptyTitle",
                    {
                      defaultValue:
                        "Sem transportes atuais",
                    }
                  )}
            </strong>

            <span>
              {searchTerm
                ? t(
                    "map.live.noResultsDescription",
                    {
                      defaultValue:
                        "Experimente outro termo de pesquisa.",
                    }
                  )
                : t(
                    "map.live.emptyDescription",
                    {
                      defaultValue:
                        "As sessões ativas aparecerão aqui automaticamente.",
                    }
                  )}
            </span>
          </div>
        )}
      </aside>

      <button
        type="button"
        className="map-view__sidebar-toggle"
        onClick={() =>
          setIsSidebarCollapsed(
            (current) => !current
          )
        }
        aria-label={
          isSidebarCollapsed
            ? t(
                "map.sidebar.show",
                {
                  defaultValue:
                    "Mostrar painel",
                }
              )
            : t(
                "map.sidebar.hide",
                {
                  defaultValue:
                    "Ocultar painel",
                }
              )
        }
        title={
          isSidebarCollapsed
            ? t(
                "map.sidebar.show",
                {
                  defaultValue:
                    "Mostrar painel",
                }
              )
            : t(
                "map.sidebar.hide",
                {
                  defaultValue:
                    "Ocultar painel",
                }
              )
        }
      >
        {isSidebarCollapsed ? (
          <ChevronRight
            size={18}
            aria-hidden="true"
          />
        ) : (
          <ChevronLeft
            size={18}
            aria-hidden="true"
          />
        )}
      </button>

      <section className="map-view__canvas">
        <div className="map-base live-transports-map__map">
          <div
            ref={mapContainerRef}
            className="map-base__canvas"
          />

          {loading &&
            !sessions.length && (
              <div className="live-transports-map__map-loading">
                <RefreshCw
                  size={24}
                  className="is-spinning"
                />

                <strong>
                  {t(
                    "map.live.loadingMap",
                    {
                      defaultValue:
                        "A preparar o mapa operacional...",
                    }
                  )}
                </strong>
              </div>
            )}

          {selectedSession && (
            <div className="live-transports-map__selection">
              <span>
                {getSessionDisplayNumber(
                  selectedSession
                )}
              </span>

              <strong>
                {selectedSession.routeName ||
                  t(
                    "map.live.withoutSavedRoute",
                    {
                      defaultValue:
                        "Rota operacional",
                    }
                  )}
              </strong>

              <small>
                {selectedSession
                  .routeTemplateId
                  ? t(
                      "map.live.savedRouteVersion",
                      {
                        version:
                          selectedSession
                            .routeTemplateVersion ||
                          1,

                        defaultValue:
                          "Rota guardada · versão {{version}}",
                      }
                    )
                  : t(
                      "map.live.calculatedRoute",
                      {
                        defaultValue:
                          "Trajeto calculado a partir dos pontos operacionais",
                      }
                    )}
              </small>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default LiveTransportsMap;